import { observer } from 'mobx-react-lite'
import Styles from '../Panels.module.scss'
import PlagiarismDetectionStyles from './PlagiarismDetection.module.scss'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import {
  PlagiarismDetectionResponse,
  PlagiarismDetectionResult,
  createPlagiarismDetectionRequest,
} from 'components/DocRightPanelWrapper/createPlagiarismDetectionRequest'
import { PropsWithChildren, useState } from 'react'
import { ReactComponent as Close } from 'assets/icons/close16.svg'
import cx from 'classnames'
import { findNodeByType } from 'common/Prose/utils/findNodeByType'
import { Node } from 'prosemirror-model'
import { schema } from 'prose-core'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button/Button'
import { CheckBroken, Scan } from 'blaze-ui/icons/20'
import { Heading } from 'blaze-ui/Heading/Heading'
import { PlagiarismCheck } from 'blaze-ui/icons/36'

const PlagiarismDetectionItem = ({ result }: { result: PlagiarismDetectionResult }) => {
  return (
    <div className={PlagiarismDetectionStyles.plagiarismResult}>
      <Text lineClamp={1} variant="smallList">
        <a href={result.url} target="_blank" rel="noopener noreferrer" className={PlagiarismDetectionStyles.title}>
          {result.title}
        </a>
      </Text>
      <Text lineClamp={1} variant="label">
        <a href={result.url} target="_blank" rel="noopener noreferrer" className={PlagiarismDetectionStyles.url}>
          {result.url}
        </a>
      </Text>
      <Text lineClamp={3} variant="secondary">
        <div className={PlagiarismDetectionStyles.content}>{result.textsnippet}</div>
      </Text>
    </div>
  )
}

export const usePlagiarsimDetection = () => {
  const [loading, setLoading] = useState(false)
  const [plagiarismResult, setPlagiarismResult] = useState<PlagiarismDetectionResponse>()
  const [hasRun, setHasRun] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const run = async (text: string) => {
    setError(undefined)
    setLoading(true)
    try {
      const res = await createPlagiarismDetectionRequest(text)
      if (res.data.error || res.type === 'error') {
        // these are errors from the copyscape API
        setError(res.data.error || 'Something went wrong')
      } else {
        setPlagiarismResult(res.data as PlagiarismDetectionResponse)
      }
    } catch {
      setPlagiarismResult(undefined)
      setError('Something went wrong')
    }
    setLoading(false)
    setHasRun(true)
  }

  return { loading, plagiarismResult, hasRun, run, error, setError }
}

export const PlagiarismDetection = observer(({ children }: PropsWithChildren) => {
  const { loading, plagiarismResult, hasRun, run, error, setError } = usePlagiarsimDetection()
  const view = useEditorView()

  const handlePress = async () => {
    const aiNode = findNodeByType(view.state.doc, schema.nodes.ai_suggestion)
    const text = view.state.doc.textBetween(0, view.state.doc.nodeSize - 2)

    if (aiNode) {
      const node = Node.fromJSON(schema, { type: 'doc', content: JSON.parse(aiNode.attrs.contentData) })
      run(node.textContent)
    } else if (text.length > 0) {
      run(text)
    } else {
      setError('Document is empty')
    }
  }

  let content

  if (error || (hasRun && !plagiarismResult?.result)) {
    content = (
      <div className={cx(PlagiarismDetectionStyles.placeholder, PlagiarismDetectionStyles.success)}>
        <div className={cx(PlagiarismDetectionStyles.svgWrapper, PlagiarismDetectionStyles.failure)}>
          <Close />
        </div>
        <Heading level={5}>
          <span className={PlagiarismDetectionStyles.loadingText}>{error}</span>
        </Heading>
        <Text variant="secondary">
          <span className={PlagiarismDetectionStyles.wordCount}>Please try again</span>
        </Text>
      </div>
    )
  } else if (loading) {
    content = (
      <div className={PlagiarismDetectionStyles.placeholder}>
        <div className={PlagiarismDetectionStyles.spinner} />
        <Text variant="secondary">
          <span className={PlagiarismDetectionStyles.loadingText}>Scanning for plagiarism...</span>
        </Text>
      </div>
    )
  } else if (!hasRun) {
    content = (
      <div className={PlagiarismDetectionStyles.placeholder}>
        <PlagiarismCheck size={45} />
        <Text variant="placeholder">Any articles or websites with content matching yours will appear here.</Text>
      </div>
    )
  } else if (hasRun && plagiarismResult?.result?.length) {
    content = (
      <div className={PlagiarismDetectionStyles.results}>
        <Text variant="largeList">Found {plagiarismResult.result.length} results</Text>
        <div className={PlagiarismDetectionStyles.resultsList}>
          {plagiarismResult.result.map((result) => (
            <PlagiarismDetectionItem result={result} key={result.index} />
          ))}
        </div>
      </div>
    )
  } else if (hasRun && !plagiarismResult?.result?.length) {
    content = (
      <div className={cx(PlagiarismDetectionStyles.placeholder, PlagiarismDetectionStyles.success)}>
        <div className={PlagiarismDetectionStyles.svgWrapper}>
          <CheckBroken size={24} />
        </div>
        <Heading level={4}>
          <span className={PlagiarismDetectionStyles.successTitle}>No plagiarism detected!</span>
        </Heading>
        <Text variant="secondary">
          <span className={PlagiarismDetectionStyles.wordCount}>{plagiarismResult?.querywords} words scanned.</span>
        </Text>
      </div>
    )
  }

  return (
    <div className={Styles.container}>
      <Text variant="primary">
        Scan the contents of this document for originality, compared against online sources.
      </Text>
      <Button size="lg" onPress={handlePress} frontIcon={Scan} isDisabled={loading}>
        Scan Document
      </Button>
      {content}
      {children}
    </div>
  )
})
